import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  DestroyRef,
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { EMPTY, Observable, Subject, of, throwError, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  first,
  map,
  retry,
  share,
  switchMap,
  takeWhile,
  timeout,
} from 'rxjs/operators';

import {
  DEFAULT_POLLING_CONFIG,
  InstagramAuthResult,
  InstagramAuthState,
  InstagramAuthStatusEnum,
  InstagramAuthUrlResponse,
  InstagramConnectionStatus,
  InstagramPollingConfig,
  ResponseInterface,
} from '@mzic/mzic-interfaces';
import { API_URL } from '@mzic/mzic-utils';

/**
 * Serviço para Instagram Graph API
 * Substitui o Basic Display API descontinuado
 *
 * Funcionalidades:
 * - Autenticação via popup OAuth
 * - Polling reativo de status
 * - Estados gerenciados com signals
 * - Tratamento de erros tipados
 * - Cleanup automático com takeUntilDestroyed
 */
@Injectable({
  providedIn: 'root',
})
export class MzicInstagramGraphService {
  private readonly _http = inject(HttpClient);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _transloco = inject(TranslocoService);

  // Configuração do polling
  private readonly _pollingConfig: InstagramPollingConfig =
    DEFAULT_POLLING_CONFIG;

  // Estados reativos com signals
  private readonly _status = signal<InstagramAuthStatusEnum>(
    InstagramAuthStatusEnum.IDLE,
  );
  private readonly _connectionStatus = signal<InstagramConnectionStatus | null>(
    null,
  );
  private readonly _authUrl = signal<string | null>(null);
  private readonly _errorMessage = signal<string>('');
  private readonly _isPolling = signal<boolean>(false);

  // Estados públicos computados
  public readonly status = this._status.asReadonly();
  public readonly connectionStatus = this._connectionStatus.asReadonly();
  public readonly authUrl = this._authUrl.asReadonly();
  public readonly errorMessage = this._errorMessage.asReadonly();
  public readonly isPolling = this._isPolling.asReadonly();

  // Estados derivados para UI
  public readonly isLoading = computed(
    () => this._status() === InstagramAuthStatusEnum.LOADING,
  );

  public readonly isConnected = computed(
    () => this._connectionStatus()?.connected === true,
  );

  public readonly hasError = computed(() => {
    const currentStatus = this._status();
    return currentStatus.includes('ERROR_');
  });

  public readonly errorType = computed(() => {
    const currentStatus = this._status();
    switch (currentStatus) {
      case InstagramAuthStatusEnum.ERROR_MISMATCH:
        return 'MISMATCH';
      case InstagramAuthStatusEnum.ERROR_ACCOUNT_TYPE:
        return 'ACCOUNT_TYPE';
      case InstagramAuthStatusEnum.ERROR_PERMISSION:
        return 'PERMISSION';
      case InstagramAuthStatusEnum.ERROR_TECHNICAL:
      default:
        return 'TECHNICAL';
    }
  });

  public readonly canRetry = computed(() => {
    const currentStatus = this._status();
    return (
      currentStatus !== InstagramAuthStatusEnum.LOADING &&
      currentStatus !== InstagramAuthStatusEnum.POLLING &&
      currentStatus !== InstagramAuthStatusEnum.SUCCESS
    );
  });

  // Subject para controle de polling
  private readonly _stopPolling$ = new Subject<void>();

  // Subject para erros de serviço (compatibilidade com padrão atual)
  public readonly serviceError$ = new Subject<string | null>();


  /**
   * Inicia o processo de autenticação do Instagram com popup síncrono
   */
  authenticateInstagram(
    artistId: string,
    popup?: Window | null,
  ): Observable<InstagramAuthResult> {
    this._resetState();
    this._status.set(InstagramAuthStatusEnum.LOADING);

    // Se popup foi fornecido, usar popup síncrono
    if (popup) {
      return this.getAuthUrlOnly(artistId).pipe(
        switchMap((authUrl) => {
          this._authUrl.set(authUrl);

          // Redirecionar popup para URL do Instagram
          if (!popup.closed) {
            popup.location.href = authUrl;
          } else {
            return throwError(
              () => new Error(this._translateErrorMessage('popup_blocked')),
            );
          }

          // Iniciar polling
          return this._startPolling(artistId).pipe(
            finalize(() => {
              this._closePopup(popup);
              this._isPolling.set(false);
              this._stopPolling$.next();
            }),
          );
        }),
        catchError((error) => {
          this._handleError(error);
          this._closePopup(popup);
          return throwError(() => error);
        }),
        takeUntilDestroyed(this._destroyRef),
      );
    }

    // Fluxo original com popup tradicional
    return this._getAuthUrl(artistId).pipe(
      switchMap((authUrlResponse) => {
        this._authUrl.set(authUrlResponse.data.authUrl);
        return this._openAuthPopupAndStartPolling(
          artistId,
          authUrlResponse.data.authUrl,
        );
      }),
      catchError((error) => {
        this._handleError(error);
        return throwError(() => error);
      }),
      takeUntilDestroyed(this._destroyRef),
    );
  }

  /**
   * Obtém URL de autenticação do backend
   */
  private _getAuthUrl(
    artistId: string,
  ): Observable<ResponseInterface<InstagramAuthUrlResponse>> {
    return this._http
      .post<
        ResponseInterface<InstagramAuthUrlResponse>
      >(API_URL.INSTAGRAM_AUTH_URL(artistId), {})
      .pipe(
        first(),
        retry(2),
        timeout(10000),
        catchError((error: HttpErrorResponse) => {
          this._handleHttpError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Verifica status da conexão Instagram
   */
  private _getConnectionStatus(
    artistId: string,
  ): Observable<ResponseInterface<InstagramConnectionStatus>> {
    return this._http
      .get<
        ResponseInterface<InstagramConnectionStatus>
      >(API_URL.INSTAGRAM_STATUS(artistId))
      .pipe(
        first(),
        timeout(5000),
        catchError((error: HttpErrorResponse) => {
          this._handleHttpError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Abre popup e inicia polling
   */
  private _openAuthPopupAndStartPolling(
    artistId: string,
    authUrl: string,
  ): Observable<InstagramAuthResult> {
    // Abre popup
    const popup = this._openAuthPopup(authUrl);

    if (!popup) {
      this._status.set(InstagramAuthStatusEnum.ERROR_TECHNICAL);
      const errorMessage = this._translateErrorMessage('popup_blocked');
      this._errorMessage.set(errorMessage);
      this.serviceError$.next(errorMessage);
      return throwError(() => new Error(errorMessage));
    }

    // Additional check for Safari popup blocking
    // Safari might return a popup object but still block it
    setTimeout(() => {
      try {
        if (popup.closed === true) {
          // Popup was immediately closed, likely blocked
          console.warn('Popup blocked, falling back to redirect flow');

          // Tentar fallback para redirect
          this._closePopup(popup);
          this.authenticateInstagramWithRedirect(artistId).subscribe();
          return;
        }
      } catch (e) {
        // Can't access popup properties, likely blocked
        console.warn('Popup access denied, falling back to redirect:', e);

        // Tentar fallback para redirect
        this._closePopup(popup);
        this.authenticateInstagramWithRedirect(artistId).subscribe();
        return;
      }
    }, 500);

    // NOVA ESTRATÉGIA: Confiar apenas no polling do endpoint de status
    // Ignorar completamente o fechamento do popup (OAuth sempre fecha popup)
    return this._startPolling(artistId).pipe(
      finalize(() => {
        this._closePopup(popup);
        this._isPolling.set(false);
        this._stopPolling$.next();
      }),
      takeUntilDestroyed(this._destroyRef),
    );
  }

  /**
   * Inicia polling para verificar status
   */
  private _startPolling(artistId: string): Observable<InstagramAuthResult> {
    this._isPolling.set(true);

    return timer(0, this._pollingConfig.intervalMs).pipe(
      takeWhile((_, index) => index < this._pollingConfig.maxAttempts),
      switchMap(() => this._getConnectionStatus(artistId)),
      map((response) => response.data),
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
      ),
      switchMap((status) => {
        this._connectionStatus.set(status);
        return this._processConnectionStatus(status);
      }),
      takeWhile((result) => !result.success && !result.error, true),
      timeout(this._pollingConfig.timeoutMs),
      catchError((error) => {
        // Outros erros (timeout, etc.)
        this._status.set(InstagramAuthStatusEnum.ERROR_TECHNICAL);
        this._errorMessage.set(this._translateErrorMessage('timeout'));
        return of({
          success: false,
          error: 'Timeout occurred',
        });
      }),
      share(),
    );
  }

  /**
   * Processa status da conexão e retorna resultado
   */
  private _processConnectionStatus(
    status: InstagramConnectionStatus,
  ): Observable<InstagramAuthResult> {
    if (status.connected) {
      this._status.set(InstagramAuthStatusEnum.SUCCESS);
      return of({
        success: true,
        connected: true,
        username: status.username,
      });
    }

    // Status false significa ainda processando ou falha
    // Continuar polling até timeout
    return EMPTY;
  }

  /**
   * Abre popup de autenticação
   */
  private _openAuthPopup(url: string): Window | null {
    const width = 600;
    const height = 700;
    const left = Math.round(window.screen.width / 2 - width / 2);
    const top = Math.round(window.screen.height / 2 - height / 2);

    try {
      const popup = window.open(
        url,
        'instagramAuth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`,
      );

      // Safari may return a popup object but still block it
      // Check if popup is actually accessible
      if (popup) {
        // Try to access popup's location to verify it's not blocked
        // This will throw an error if popup is blocked in Safari
        setTimeout(() => {
          try {
            // If we can access the popup's closed property, it's likely working
            if (popup.closed !== false) {
              console.warn('Popup may have been blocked');
            }
          } catch (e) {
            console.warn('Popup blocked or restricted:', e);
          }
        }, 100);
      }

      return popup;
    } catch (error) {
      console.error('Error opening popup:', error);
      return null;
    }
  }

  /**
   * Fecha popup
   */
  private _closePopup(popup: Window | null): void {
    if (popup && !popup.closed) {
      popup.close();
    }
  }

  /**
   * Gera HTML simples de loading para o popup
   */
  private _generateLoadingHTML(): string {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Instagram</title>
        </head>
        <body>
        </body>
      </html>
    `;
  }

  /**
   * Abre popup síncrono com página de loading
   */
  public openSyncPopup(): Window | null {
    const width = 600;
    const height = 700;
    const left = Math.round(window.screen.width / 2 - width / 2);
    const top = Math.round(window.screen.height / 2 - height / 2);

    try {
      const popup = window.open(
        'about:blank',
        'instagramAuth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`,
      );

      if (popup) {
        // Escrever HTML de loading imediatamente
        popup.document.write(this._generateLoadingHTML());
        popup.document.close();
      }

      return popup;
    } catch (error) {
      console.error('Error opening sync popup:', error);
      return null;
    }
  }

  /**
   * Reset do estado
   */
  private _resetState(): void {
    this._status.set(InstagramAuthStatusEnum.IDLE);
    this._connectionStatus.set(null);
    this._authUrl.set(null);
    this._errorMessage.set('');
    this._isPolling.set(false);
    this._stopPolling$.next();
  }

  /**
   * Traduz mensagem de erro baseada no tipo
   */
  private _translateErrorMessage(
    errorType:
      | 'technical'
      | 'connection'
      | 'server'
      | 'timeout'
      | 'popup_blocked'
      | 'duplicate_records',
  ): string {
    const key = `error_${errorType}`;
    return this._transloco.translate(key, {}, 'mzic-connect-your-instagram');
  }

  /**
   * Tratamento de erros HTTP
   */
  private _handleHttpError(error: HttpErrorResponse): void {
    let errorMessage: string;

    // Verificar se é erro de Instagram (código 0007)
    if (error.error?.error?.code === '0007') {
      errorMessage = this._translateErrorMessage('technical');
      this._status.set(InstagramAuthStatusEnum.ERROR_TECHNICAL);
    } else if (error.error?.error?.error) {
      errorMessage = error.error.error.error;
    } else if (error.error?.error && typeof error.error.error === 'string') {
      errorMessage = error.error.error;
    } else if (error.error && typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 0) {
      errorMessage = this._translateErrorMessage('connection');
    } else if (error.status >= 500) {
      errorMessage = this._translateErrorMessage('server');
    } else {
      errorMessage = this._translateErrorMessage('technical');
    }

    this._errorMessage.set(errorMessage);
    this.serviceError$.next(errorMessage);
  }

  /**
   * Tratamento de erros gerais
   */
  private _handleError(error: unknown): void {
    this._status.set(InstagramAuthStatusEnum.ERROR_TECHNICAL);

    const errorMessage =
      (error as Error)?.message || this._translateErrorMessage('technical');
    this._errorMessage.set(errorMessage);
    this.serviceError$.next(errorMessage);
  }

  /**
   * Limpar estado e parar polling
   */
  public stopAuthentication(): void {
    this._stopPolling$.next();
    this._resetState();
  }

  /**
   * Tentar novamente autenticação
   */
  public retryAuthentication(
    artistId: string,
  ): Observable<InstagramAuthResult> {
    this.stopAuthentication();
    return this.authenticateInstagram(artistId);
  }

  /**
   * Autentica com Instagram usando redirect (funciona em Safari)
   */
  public authenticateInstagramWithRedirect(
    artistId: string,
  ): Observable<never> {
    this._resetState();
    this._status.set(InstagramAuthStatusEnum.LOADING);

    return this._getAuthUrl(artistId).pipe(
      switchMap((authUrlResponse) => {
        this._authUrl.set(authUrlResponse.data.authUrl);

        // Redirecionar para Instagram
        window.location.href = authUrlResponse.data.authUrl;

        // Retornar observable que nunca emite (pois está redirecionando)
        return EMPTY;
      }),
      catchError((error) => {
        this._handleError(error);
        return throwError(() => error);
      }),
      takeUntilDestroyed(this._destroyRef),
    );
  }

  /**
   * Inicia polling após retorno do redirect (para ser chamado no ngOnInit)
   */
  public startPollingAfterRedirect(
    artistId: string,
  ): Observable<InstagramAuthResult> {
    this._resetState();
    this._status.set(InstagramAuthStatusEnum.POLLING);

    return this._startPolling(artistId).pipe(
      takeUntilDestroyed(this._destroyRef),
    );
  }

  /**
   * Obtém apenas a URL de autenticação (para popup síncrono)
   */
  public getAuthUrlOnly(artistId: string): Observable<string> {
    this._status.set(InstagramAuthStatusEnum.LOADING);

    return this._getAuthUrl(artistId).pipe(
      map((response) => response.data.authUrl),
      catchError((error) => {
        this._handleError(error);
        return throwError(() => error);
      }),
      takeUntilDestroyed(this._destroyRef),
    );
  }

  /**
   * Obter estado atual como objeto (para compatibilidade)
   */
  public getCurrentState(): InstagramAuthState {
    return {
      isLoading: this.isLoading(),
      isPolling: this.isPolling(),
      hasError: this.hasError(),
      errorMessage: this.errorMessage(),
      successMessage: this.isConnected()
        ? 'Conectado ao Instagram com sucesso!'
        : '',
      authUrl: this.authUrl(),
      status: this.connectionStatus(),
    };
  }
}
